import { Loading } from 'pages/loading/ui/loading.page';
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CardWithOverlay,
  PageLayout,
  TransparentButton,
  ModalWindow,
} from 'shared/ui';
import { Header } from 'widgets';
import styles from './point-employee-schedules.page.module.scss';
import { BookingApi } from 'shared/api';
import {
  IEmployeesMonthSchedule,
  IEmployee,
  IEmployeeDay,
} from 'shared/api/booking/interfaces/get-employees-month-schedule.interface';
import { MonthCalendarDayType } from 'shared/api/booking/interfaces/month-calendar-day-type.enum';
import { EmployeeScheduleCard } from 'features/employee-schedule-card';
import { MonthNames } from 'shared/consts/calendar.consts';
import {
  ICalendarDayBookings,
  ICalendarDayBreaks,
} from 'shared/api/booking/interfaces/calendar-day.type';
import { LeftArrowSvg, RightArrowSvg } from 'shared/svg';
import { EmployeeWorkDayPopup } from 'shared/ui/employee-work-day-popup/employee-work-day-popup';

export const PointEmployeeSchedulesPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [todayKey, setTodayKey] = useState(0);
  const [scheduleData, setScheduleData] =
    useState<IEmployeesMonthSchedule | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<{
    employeeId: string;
    employee: IEmployee;
    day: IEmployeeDay;
    date: Date;
  } | null>(null);

  const month = currentDate.getMonth() + 1;
  const year = currentDate.getFullYear();

  const fetchSchedule = useCallback(async () => {
    if (!id) return;

    setIsLoading(true);
    setError(null);

    try {
      const data = await BookingApi.getEmployeesMonthSchedule({
        month,
        year,
        pointId: id,
      });
      setScheduleData(data);
    } catch (err) {
      setError('Не удалось загрузить графики сотрудников');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [id, month, year]);

  useEffect(() => {
    if (id) {
      fetchSchedule();
    }
  }, [id, month, year]);

  const goToPrevMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() - 1);
    setCurrentDate(newDate);
  };

  const goToNextMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + 1);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setTodayKey((prev) => prev + 1);
  };

  const handleDayClick = (
    employeeId: string,
    day: IEmployeeDay,
    dayDate: Date
  ) => {
    const emp = scheduleData?.employees.find((e) => e.id === employeeId);
    if (emp) {
      setSelectedDay({ employeeId, employee: emp, day, date: dayDate });
    }
  };

  const handleClosePopup = () => setSelectedDay(null);
  const handleUpdateSchedule = () => fetchSchedule();

  if (!id) {
    return <Loading />;
  }

  return (
    <PageLayout>
      <Header />
      <CardWithOverlay title="Графики работы сотрудников">
        <div className={styles.page__content}>
          <TransparentButton
            text="Назад"
            onClick={() => navigate(-1)}
            className={styles.button_back}
          />
          <div className={styles.page__header}>
            <div className={styles.month__navigation}>
              <div className={styles.navigation__arrow} onClick={goToPrevMonth}>
                <LeftArrowSvg />
              </div>
              <div className={styles.navigation__month_year}>
                <span className={styles.navigation__month}>
                  {MonthNames[currentDate.getMonth()]}
                </span>
                <span className={styles.navigation__year}>{year}</span>
              </div>
              <div className={styles.navigation__arrow} onClick={goToNextMonth}>
                <RightArrowSvg />
              </div>
              <div className={styles.navigation__today}>
                <button className={styles.today__button} onClick={goToToday}>
                  Сегодня
                </button>
              </div>
            </div>
          </div>

          {isLoading && (
            <div className={styles.page__loading}>
              <Loading />
            </div>
          )}

          {error && <div className={styles.page__error}>{error}</div>}

          {!error && scheduleData && (
            <>
              <div className={styles.legend}>
                <div className={styles.legend__item}>
                  <div className={styles.legend__color_legend_workday}></div>
                  <span>Рабочий день</span>
                </div>
                <div className={styles.legend__item}>
                  <div className={styles.legend__color_legend_dayoff}></div>
                  <span>Выходной</span>
                </div>
                <div className={styles.legend__item}>
                  <div className={styles.legend__color_legend_vacation}></div>
                  <span>Отпуск</span>
                </div>
                <div className={styles.legend__item}>
                  <div
                    className={styles.legend__color_legend_administrative}
                  ></div>
                  <span>Административный</span>
                </div>
                <div className={styles.legend__item}>
                  <div className={styles.legend__color_legend_sickleave}></div>
                  <span>Больничный</span>
                </div>
              </div>
              <div className={styles.page__list}>
                {scheduleData.employees.length === 0 ? (
                  <div className={styles.page__empty}>
                    Нет сотрудников для отображения
                  </div>
                ) : (
                  scheduleData.employees.map((employee) => (
                    <EmployeeScheduleCard
                      key={`${employee.id}-${todayKey}`}
                      employee={employee}
                      currentDate={currentDate}
                      onDayClick={handleDayClick}
                    />
                  ))
                )}
              </div>
            </>
          )}
        </div>
        {selectedDay && (
          <ModalWindow onClickOutside={handleClosePopup}>
            <EmployeeWorkDayPopup
              employee={{
                employee: {
                  id: selectedDay.employee.id,
                  fio: selectedDay.employee.fio,
                  imgUrl: selectedDay.employee.imgUrl,
                },
                workDay: {
                  startedAt: selectedDay.day.from || null,
                  finishedAt: selectedDay.day.to || null,
                  isDayOff:
                    selectedDay.day.type === MonthCalendarDayType.DAY_OFF,
                },
                breaks: [] as unknown as [ICalendarDayBreaks],
                bookings: [] as unknown as [ICalendarDayBookings],
              }}
              date={selectedDay.date}
              onClose={handleClosePopup}
              onUpdate={handleUpdateSchedule}
            />
          </ModalWindow>
        )}
      </CardWithOverlay>
    </PageLayout>
  );
};
